import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Scissors,
  ArrowLeft,
  Play,
  Users,
  User,
  Building2,
  TrendingUp,
  Presentation,
  BookOpen,
  Star,
  Gift,
  Calendar,
  DollarSign,
  Crown,
  ChevronRight,
  ExternalLink,
  Monitor,
  Smartphone,
  CheckCircle,
  Sparkles,
  Target,
  BarChart3,
  Shield,
  Clock,
  Share2,
  Camera,
  CreditCard,
  Rocket,
  Award,
  Zap,
  Globe,
  Heart,
  FileText,
  Code,
  Handshake,
  PieChart,
  Mail,
  Copy,
  Linkedin,
  Twitter,
  MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import AppIntroduction from "@/components/AppIntroduction";
import GetStartedGuide, { HowItWorksSection } from "@/components/GetStartedGuide";

const DemoPackagePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDemo, setActiveDemo] = useState(null);
  const [showAppIntro, setShowAppIntro] = useState(false);
  const [showGuide, setShowGuide] = useState(null); // 'client', 'stylist', 'owner'

  // Handle hash navigation (e.g., /demo-package#soldiers)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      // Set active demo based on hash
      const validDemos = ['client', 'stylist', 'owner', 'booth', 'soldiers', 'investors', 'dev-partners'];
      if (validDemos.includes(hash)) {
        setActiveDemo(hash);
        // Scroll to the demo section after a brief delay
        setTimeout(() => {
          const element = document.getElementById('demo-content');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [location.hash]);

  // Partnership sharing content
  const partnershipShareData = {
    url: "https://myhairchair.com/partnership",
    subject: "Exciting Tech Partnership Opportunity - MyHairChair Lead Developer",
    shortText: "Check out this Lead Developer Partnership opportunity with MyHairChair - 15% equity + 15% profit share!",
    fullLetter: `Hi,

I wanted to share an exciting partnership opportunity that I think would be perfect for you.

MyHairChair (a GoNowBaby Media Group brand) is looking for a Lead Developer to join as a technical co-founder. Here's the offer:

📊 HYBRID COMPENSATION MODEL:
• 15% Net Profit Share (Year 1) - Quarterly distributions
• 15% Equity Stake - Vesting over 3 years with 1-year cliff

💰 3-YEAR POTENTIAL:
• ~$1.47M in profit share (cash)
• ~$10.8M in equity value (at exit)
• Total potential: ~$12.3M

📈 BUSINESS PROJECTIONS:
• $18M ARR target by Year 3
• 150,000 users
• 77%+ profit margins

The platform is already built with a fully functional salon management system including booking, payments, loyalty programs, and more.

View the full proposal here: https://myhairchair.com/partnership

Let me know if you'd like to discuss!

Best regards`
  };

  // Investor sharing content
  const investorShareData = {
    url: "https://myhairchair.com/investors",
    subject: "Investment Opportunity - MyHairChair | $18M ARR by Year 3",
    shortText: "Check out this investment opportunity with MyHairChair - Disrupting the $97B salon industry with projected $18M ARR by Year 3!",
    fullLetter: `Hi,

I wanted to share an exciting investment opportunity in the beauty & salon technology space.

MyHairChair (a GoNowBaby Media Group brand) is disrupting the $97B salon industry with a comprehensive management platform for stylists, barbers, and salon owners.

📊 MARKET OPPORTUNITY:
• 1.1M+ addressable professionals in the US
• Competitors (Square, Vagaro, StyleSeat, Booksy) extract $33M+ annually in fees
• MyHairChair offers 0% platform fees with payment flexibility

💰 FINANCIAL PROJECTIONS:
• Year 1: $1.8M revenue (4,000 paid subscribers)
• Year 2: $6.5M revenue (15,000 paid subscribers)
• Year 3: $18M ARR (40,000 paid subscribers)
• 77%+ profit margins

📈 COMPETITIVE ADVANTAGE:
• Payment freedom (Zelle, CashApp, PayPal, Cash - no forced processing)
• Instant payouts vs 2-3 day delays from competitors
• All-in-one platform: Booking, CRM, Loyalty, Booth Rent, Staff Management

🎯 TRACTION:
• MVP fully built and operational
• Growing user base
• Strong customer satisfaction

View the full investor presentation here: https://myhairchair.com/investors

I'd love to discuss this opportunity with you!

Best regards`
  };

  // Share functions
  const handleShare = (platform, type = 'partnership') => {
    const shareData = type === 'investor' ? investorShareData : partnershipShareData;
    const { url, subject, shortText, fullLetter } = shareData;
    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(fullLetter);
    const encodedShortText = encodeURIComponent(shortText);
    
    switch(platform) {
      case 'copy':
        navigator.clipboard.writeText(`${shortText}\n\n${url}`);
        toast.success('Link copied to clipboard!');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
        break;
      case 'gmail':
        window.open(`https://mail.google.com/mail/?view=cm&fs=1&su=${encodedSubject}&body=${encodedBody}`, '_blank');
        break;
      case 'outlook':
        window.open(`https://outlook.live.com/mail/0/deeplink/compose?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
        break;
      case 'yahoo':
        window.open(`https://compose.mail.yahoo.com/?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedShortText}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shortText + '\n\n' + url)}`, '_blank');
        break;
      default:
        break;
    }
  };

  // Demo categories
  const demoCategories = [
    {
      id: "overview",
      title: "Platform Overview",
      icon: Presentation,
      color: "from-amber-500 to-yellow-500",
      description: "Start here - Perfect for introducing MyHairChair",
      badge: "Start Here",
      badgeColor: "bg-green-500"
    },
    {
      id: "clients",
      title: "For Clients",
      icon: User,
      color: "from-blue-500 to-cyan-500",
      description: "Show clients how to find stylists and book appointments",
      badge: null
    },
    {
      id: "stylists",
      title: "For Barbers & Stylists",
      icon: Scissors,
      color: "from-amber-500 to-orange-500",
      description: "Demonstrate the stylist dashboard and booking management",
      badge: "Most Popular",
      badgeColor: "bg-purple-500"
    },
    {
      id: "owners",
      title: "For Salon Owners",
      icon: Building2,
      color: "from-purple-500 to-pink-500",
      description: "Showcase booth management and business tools",
      badge: null
    },
    {
      id: "soldiers",
      title: "HairChair Soldiers",
      icon: Shield,
      color: "from-zinc-700 to-zinc-900",
      description: "Exclusive Founding Partner affiliate program for salon owners",
      badge: "NEW",
      badgeColor: "bg-amber-500"
    },
    {
      id: "investors",
      title: "Investor Presentation",
      icon: TrendingUp,
      color: "from-emerald-500 to-teal-500",
      description: "Full business pitch deck with market analysis",
      badge: "Premium",
      badgeColor: "bg-amber-500"
    },
    {
      id: "dev-partners",
      title: "Lead Developer Partnership",
      icon: Code,
      color: "from-purple-500 to-pink-500",
      description: "15% equity + 15% profit share hybrid model",
      badge: "Partnership",
      badgeColor: "bg-purple-500"
    }
  ];

  // Platform Overview Content
  const overviewContent = {
    keyStats: [
      { label: "Active Users", value: "10,000+", icon: Users },
      { label: "Appointments Booked", value: "50,000+", icon: Calendar },
      { label: "Stylists Onboarded", value: "2,500+", icon: Scissors },
      { label: "Customer Satisfaction", value: "98%", icon: Star }
    ],
    newFeatures: [
      {
        title: "Walk-in Queue Management",
        description: "In-person client queue with estimated wait times and SMS notifications when it's their turn",
        icon: Clock,
        color: "text-green-400",
        isNew: true
      },
      {
        title: "Client Invitation Suite",
        description: "QR codes, bulk SMS/Email invitations, and shareable digital business cards",
        icon: Share2,
        color: "text-cyan-400",
        isNew: true
      },
      {
        title: "Enhanced Mobile Experience",
        description: "Mobile-first bottom navigation bar and optimized dashboard layouts for all devices",
        icon: Smartphone,
        color: "text-purple-400",
        isNew: true
      }
    ],
    features: [
      {
        title: "Easy Online Booking",
        description: "Clients book appointments 24/7 from any device",
        icon: Calendar,
        color: "text-blue-400"
      },
      {
        title: "Payment Processing",
        description: "Accept Stripe, PayPal, Cash App, Zelle & more",
        icon: CreditCard,
        color: "text-green-400"
      },
      {
        title: "Loyalty Rewards",
        description: "Built-in rewards program to retain clients",
        icon: Gift,
        color: "text-purple-400"
      },
      {
        title: "Marketing Tools",
        description: "Email & SMS campaigns, CRM, and analytics",
        icon: Target,
        color: "text-amber-400"
      },
      {
        title: "Booth Management",
        description: "Track renters, collect payments automatically",
        icon: Building2,
        color: "text-pink-400"
      },
      {
        title: "Google Calendar Sync",
        description: "Two-way sync with personal calendars",
        icon: Calendar,
        color: "text-cyan-400"
      }
    ],
    differentiators: [
      "No per-transaction fees - flat monthly pricing",
      "Built by Barbers and Stylists, for Barbers and Stylists",
      "30-day free trial for all plans",
      "Multi-platform: Web, iOS-ready, Android-ready",
      "White-label options for salon chains",
      "24/7 customer support"
    ],
    competitiveComparison: [
      { feature: "Online Booking", myHairChair: true, booksy: true, styleSeat: true, vagaro: true, square: true },
      { feature: "Guest Booking (No Account)", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: true, highlight: true },
      { feature: "Pro Mini-Site (Custom URL)", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Shareable QR Code Cards", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "QR Code Client Invites", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Digital Business Cards", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Staff Commission Tracking", myHairChair: true, booksy: false, styleSeat: false, vagaro: true, square: false, highlight: true },
      { feature: "Payroll & W-9 Management", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Loyalty/Rewards Program", myHairChair: true, booksy: true, styleSeat: true, vagaro: true, square: false },
      { feature: "Walk-in Queue Management", myHairChair: true, booksy: false, styleSeat: false, vagaro: true, square: false, highlight: true },
      { feature: "Booth/Chair Rental Mgmt", myHairChair: true, booksy: false, styleSeat: false, vagaro: true, square: false },
      { feature: "Multi-Payment (Zelle/CashApp)", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Digital Marketing Toolkit", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Referral System", myHairChair: true, booksy: false, styleSeat: true, vagaro: false, square: false },
      { feature: "PWA Support", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true },
      { feature: "Google Calendar Sync", myHairChair: true, booksy: true, styleSeat: true, vagaro: true, square: true },
      { feature: "Zero Transaction Fees", myHairChair: true, booksy: false, styleSeat: false, vagaro: false, square: false, highlight: true }
    ]
  };

  // Client Demo Content
  const clientContent = {
    steps: [
      {
        title: "1. Find a Stylist",
        description: "Browse stylists by location, specialty, rating, or price. View portfolios and reviews.",
        icon: Users,
        action: "Show: /find-stylists page"
      },
      {
        title: "2. Book Appointment",
        description: "Select service, pick date/time, add notes. Instant confirmation.",
        icon: Calendar,
        action: "Show: /book page"
      },
      {
        title: "3. Earn Rewards",
        description: "Every visit earns loyalty points. Redeem for discounts and free services.",
        icon: Gift,
        action: "Show: Client dashboard loyalty section"
      },
      {
        title: "4. Leave Reviews",
        description: "Rate your experience and help others find great stylists.",
        icon: Star,
        action: "Show: Review system"
      },
      {
        title: "5. Refer Friends",
        description: "Earn bonus points for every friend you refer who books.",
        icon: Share2,
        action: "Show: Referral dashboard"
      }
    ],
    benefits: [
      "Book appointments 24/7 - no phone calls needed",
      "See real-time availability",
      "Automatic appointment reminders",
      "Easy rebooking for regular appointments",
      "Save favorite stylists",
      "Track appointment history"
    ]
  };

  // Stylist Demo Content
  const stylistContent = {
    dashboardSections: [
      { name: "Dashboard Overview", path: "/stylist", description: "Daily appointments, revenue, pending requests" },
      { name: "Services & Pricing", path: "/stylist", description: "Add/edit services with templates" },
      { name: "Schedule Settings", path: "/stylist", description: "Set availability, block time off" },
      { name: "Client Notes", path: "/stylist", description: "Track client preferences and history" },
      { name: "Photo Gallery", path: "/stylist", description: "Showcase your best work" },
      { name: "Revenue & Analytics", path: "/stylist", description: "Track earnings and growth" },
      { name: "Marketing & CRM", path: "/stylist", description: "Email/SMS campaigns" },
      { name: "Rewards Engine", path: "/stylist", description: "Configure loyalty program" },
      { name: "Referral Dashboard", path: "/stylist", description: "Track referrals and earnings" },
      { name: "Waitlist & Walk-ins", path: "/stylist", description: "Queue management with SMS notifications", isNew: true },
      { name: "Invite Clients", path: "/stylist", description: "QR codes, SMS/Email blasts, digital business card", isNew: true },
      { name: "Pro Mini-Site", path: "/stylist", description: "Custom URL with QR code & booking page", isNew: true }
    ],
    pricingTiers: [
      {
        name: "Pro",
        price: "$29.99/mo",
        highlight: true,
        features: [
          "30-day FREE trial",
          "Unlimited clients", 
          "All payment methods (Zelle, CashApp)", 
          "CRM Dashboard", 
          "Calendar sync", 
          "Pro Mini-Site (Custom URL)", 
          "Shareable QR Code Cards",
          "Portfolio Gallery",
          "Loyalty rewards program",
          "No branding"
        ]
      },
      {
        name: "Elite",
        price: "$49.99/mo",
        features: [
          "30-day FREE trial",
          "Everything in Pro", 
          "Staff Management Dashboard",
          "Commission Tracking (Barbers/Stylists)",
          "Support Staff (Hourly/Flat Rate)",
          "Payroll & W-9 Tax Compliance",
          "Booth/Chair Management", 
          "Automated Rent Collection",
          "Team Performance Analytics", 
          "Digital Marketing Toolkit",
          "Accounting Dashboard",
          "Custom Branding Colors"
        ]
      }
    ]
  };

  // Owner Demo Content
  const ownerContent = {
    features: [
      {
        title: "Staff Management Dashboard",
        description: "Manage all your barbers, stylists, and support staff in one place",
        icon: Users,
        isNew: true
      },
      {
        title: "Commission Tracking",
        description: "Set up percentage or flat-rate commissions for each stylist",
        icon: DollarSign,
        isNew: true
      },
      {
        title: "Payroll & W-9 Compliance",
        description: "Track YTD earnings, auto-flag >$600 for W-9 collection",
        icon: FileText,
        isNew: true
      },
      {
        title: "Booth/Chair Management",
        description: "Add booth renters, set rent amounts, track occupancy",
        icon: Building2
      },
      {
        title: "Automated Rent Collection",
        description: "Set up recurring payments, send reminders automatically",
        icon: CreditCard
      },
      {
        title: "Team Performance Analytics",
        description: "View individual stylist metrics, revenue, and productivity",
        icon: BarChart3
      },
      {
        title: "Financial Reports",
        description: "Export P&L statements, track expenses for taxes",
        icon: DollarSign
      },
      {
        title: "Digital Marketing Toolkit",
        description: "Create shareable social cards, run email/SMS campaigns",
        icon: Target
      },
      {
        title: "Pro Mini-Site for All Staff",
        description: "Each stylist gets their own custom URL with QR code",
        icon: Globe,
        isNew: true
      }
    ],
    roi: [
      { metric: "Time Saved", value: "10+ hrs/week", description: "On admin tasks" },
      { metric: "Missed Appointments", value: "-40%", description: "With reminders" },
      { metric: "Client Retention", value: "+67%", description: "With loyalty program" },
      { metric: "Revenue Growth", value: "+25%", description: "Average first year" }
    ]
  };

  const renderDemoContent = () => {
    switch (activeDemo) {
      case "overview":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => setShowAppIntro(true)}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black"
              >
                <Play className="w-4 h-4 mr-2" />
                Play App Introduction
              </Button>
              <Link to="/">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View Landing Page
                </Button>
              </Link>
            </div>

            {/* New Features Highlight */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-green-400" />
                What&apos;s New
                <Badge className="bg-green-500 text-white text-xs ml-2">Latest Updates</Badge>
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {overviewContent.newFeatures.map((feature, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30 hover:border-green-400/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <feature.icon className={`w-5 h-5 ${feature.color}`} />
                        <Badge className="bg-green-500 text-white text-xs">NEW</Badge>
                      </div>
                      <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-400">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Key Stats */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Platform Highlights</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {overviewContent.keyStats.map((stat, idx) => (
                  <Card key={idx} className="bg-stone-800/50 border-stone-700">
                    <CardContent className="p-4 text-center">
                      <stat.icon className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-sm text-gray-400">{stat.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Core Features */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Core Features</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {overviewContent.features.map((feature, idx) => (
                  <Card key={idx} className="bg-stone-800/50 border-stone-700">
                    <CardContent className="p-4">
                      <feature.icon className={`w-6 h-6 ${feature.color} mb-2`} />
                      <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
                      <p className="text-sm text-gray-400">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Differentiators */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Why MyHairChair?</h3>
              <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-3">
                    {overviewContent.differentiators.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Competitive Comparison */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-400" />
                Competitive Analysis
              </h3>
              <Card className="bg-stone-800/50 border-stone-700 overflow-hidden">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-purple-500/20 border-b border-stone-700">
                          <th className="text-left p-3 text-white font-semibold">Feature</th>
                          <th className="p-3 text-center text-amber-400 font-bold">MyHairChair</th>
                          <th className="p-3 text-center text-gray-400">Booksy</th>
                          <th className="p-3 text-center text-gray-400">StyleSeat</th>
                          <th className="p-3 text-center text-gray-400">Vagaro</th>
                          <th className="p-3 text-center text-gray-400">Square</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overviewContent.competitiveComparison.map((row, idx) => (
                          <tr key={idx} className={`border-b border-stone-700/50 ${row.highlight ? 'bg-amber-500/10' : ''}`}>
                            <td className={`p-3 ${row.highlight ? 'text-white font-semibold' : 'text-gray-300'}`}>
                              {row.feature}
                            </td>
                            <td className="p-3 text-center">
                              {row.myHairChair ? (
                                <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                              ) : (
                                <span className="text-red-400">✗</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.booksy ? (
                                <CheckCircle className="w-5 h-5 text-green-400/60 mx-auto" />
                              ) : (
                                <span className="text-red-400/60">✗</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.styleSeat ? (
                                <CheckCircle className="w-5 h-5 text-green-400/60 mx-auto" />
                              ) : (
                                <span className="text-red-400/60">✗</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.vagaro ? (
                                <CheckCircle className="w-5 h-5 text-green-400/60 mx-auto" />
                              ) : (
                                <span className="text-red-400/60">✗</span>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {row.square ? (
                                <CheckCircle className="w-5 h-5 text-green-400/60 mx-auto" />
                              ) : (
                                <span className="text-red-400/60">✗</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-t border-amber-500/30 text-center">
                    <span className="text-amber-400 font-bold">🏆 12 Unique Features</span>
                    <span className="text-gray-400"> that no single competitor offers together</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "clients":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => setShowGuide('client')}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
              >
                <Play className="w-4 h-4 mr-2" />
                Play Client Guide
              </Button>
              <Link to="/find-stylists">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <Users className="w-4 h-4 mr-2" />
                  Find Stylists Page
                </Button>
              </Link>
              <Link to="/book">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <Calendar className="w-4 h-4 mr-2" />
                  Booking Page
                </Button>
              </Link>
            </div>

            {/* Client Journey Steps */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Client Journey Demo</h3>
              <div className="space-y-4">
                {clientContent.steps.map((step, idx) => (
                  <Card key={idx} className="bg-stone-800/50 border-stone-700 hover:border-blue-500/50 transition-colors">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                        <step.icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold">{step.title}</h4>
                        <p className="text-gray-400 text-sm mb-2">{step.description}</p>
                        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                          {step.action}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Client Benefits</h3>
              <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-3">
                    {clientContent.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0" />
                        <span className="text-gray-300">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case "stylists":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => setShowGuide('stylist')}
                className="bg-gradient-to-r from-amber-500 to-orange-500 text-black"
              >
                <Play className="w-4 h-4 mr-2" />
                Play Stylist Guide
              </Button>
              <Link to="/onboarding-guide">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Full Onboarding Guide
                </Button>
              </Link>
              <Link to="/stylist">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <Monitor className="w-4 h-4 mr-2" />
                  Stylist Dashboard
                </Button>
              </Link>
            </div>

            {/* Dashboard Sections */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Dashboard Features to Demo</h3>
              <div className="grid md:grid-cols-3 gap-3">
                {stylistContent.dashboardSections.map((section, idx) => (
                  <Card key={idx} className={`bg-stone-800/50 border-stone-700 hover:border-amber-500/50 transition-colors cursor-pointer ${section.isNew ? 'ring-1 ring-green-500/30' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <h4 className="text-white font-semibold mb-1">{section.name}</h4>
                        {section.isNew && (
                          <Badge className="bg-green-500 text-white text-xs">NEW</Badge>
                        )}
                      </div>
                      <p className="text-gray-400 text-sm">{section.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Pricing Tiers */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Pricing Plans</h3>
              <div className="grid md:grid-cols-3 gap-4">
                {stylistContent.pricingTiers.map((tier, idx) => (
                  <Card key={idx} className={`border ${tier.highlight ? 'border-amber-500/50 bg-amber-500/10' : 'border-stone-700 bg-stone-800/50'}`}>
                    <CardContent className="p-6 text-center">
                      {tier.highlight && (
                        <Badge className="bg-amber-500 text-black mb-2">Most Popular</Badge>
                      )}
                      <h4 className="text-xl font-bold text-white mb-1">{tier.name}</h4>
                      <p className="text-2xl font-bold text-amber-400 mb-4">{tier.price}</p>
                      <ul className="space-y-2 text-left">
                        {tier.features.map((f, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );

      case "owners":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Button 
                onClick={() => setShowGuide('owner')}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              >
                <Play className="w-4 h-4 mr-2" />
                Play Owner Guide
              </Button>
              <Link to="/stylist">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <Building2 className="w-4 h-4 mr-2" />
                  Owner Dashboard
                </Button>
              </Link>
            </div>

            {/* Owner Features */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">Salon Owner Features</h3>
              <div className="grid md:grid-cols-2 gap-4">
                {ownerContent.features.map((feature, idx) => (
                  <Card key={idx} className="bg-stone-800/50 border-stone-700">
                    <CardContent className="p-4 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold">{feature.title}</h4>
                        <p className="text-gray-400 text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* ROI Metrics */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">ROI for Salon Owners</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ownerContent.roi.map((item, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-purple-400">{item.value}</p>
                      <p className="text-white font-medium">{item.metric}</p>
                      <p className="text-xs text-gray-400">{item.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );

      case "investors":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Link to="/investors">
                <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                  <Presentation className="w-4 h-4 mr-2" />
                  Full Investor Presentation
                </Button>
              </Link>
              <Link to="/investors">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Download PDF Deck
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Opportunity
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-stone-900 border-stone-700">
                  <div className="px-2 py-1.5 text-xs text-gray-400">Email Platforms</div>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('gmail', 'investor')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-red-400" />
                    Send via Gmail
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('outlook', 'investor')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-blue-400" />
                    Send via Outlook
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('yahoo', 'investor')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-purple-400" />
                    Send via Yahoo Mail
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('email', 'investor')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    Default Email App
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-stone-700" />
                  <div className="px-2 py-1.5 text-xs text-gray-400">Social Media</div>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('linkedin', 'investor')}
                  >
                    <Linkedin className="w-4 h-4 mr-2 text-blue-500" />
                    Share on LinkedIn
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('twitter', 'investor')}
                  >
                    <Twitter className="w-4 h-4 mr-2 text-sky-400" />
                    Share on X/Twitter
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('whatsapp', 'investor')}
                  >
                    <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                    Share on WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-stone-700" />
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('copy', 'investor')}
                  >
                    <Copy className="w-4 h-4 mr-2 text-amber-400" />
                    Copy Link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Investor Highlights */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                  Investment Highlights
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-emerald-400 font-semibold mb-2">Market Opportunity</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        $47B beauty services market
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        900K+ barber shops in the US
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        Growing demand for digital booking
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-emerald-400 font-semibold mb-2">Traction</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        10,000+ active users
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        $500K+ GMV processed
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        98% customer satisfaction
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6 text-center">
                <Award className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Ready to Invest?</h4>
                <p className="text-gray-400 mb-4">View the full investor presentation with market analysis, financial projections, and team details.</p>
                <Link to="/investors">
                  <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black">
                    View Full Presentation
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        );

      case "dev-partners":
        return (
          <div className="space-y-8">
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-4">
              <Link to="/partnership">
                <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <Code className="w-4 h-4 mr-2" />
                  Full Partnership Proposal
                </Button>
              </Link>
              <Link to="/partnership">
                <Button variant="outline" className="border-stone-600 text-white hover:bg-stone-800">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Download PDF Deck
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Opportunity
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-stone-900 border-stone-700">
                  <div className="px-2 py-1.5 text-xs text-gray-400">Email Platforms</div>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('gmail', 'partnership')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-red-400" />
                    Send via Gmail
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('outlook', 'partnership')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-blue-400" />
                    Send via Outlook
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('yahoo', 'partnership')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-purple-400" />
                    Send via Yahoo Mail
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('email', 'partnership')}
                  >
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    Default Email App
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-stone-700" />
                  <div className="px-2 py-1.5 text-xs text-gray-400">Social Media</div>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('linkedin', 'partnership')}
                  >
                    <Linkedin className="w-4 h-4 mr-2 text-blue-500" />
                    Share on LinkedIn
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('twitter', 'partnership')}
                  >
                    <Twitter className="w-4 h-4 mr-2 text-sky-400" />
                    Share on X/Twitter
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('whatsapp', 'partnership')}
                  >
                    <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                    Share on WhatsApp
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-stone-700" />
                  <DropdownMenuItem 
                    className="text-white hover:bg-stone-800 cursor-pointer"
                    onClick={() => handleShare('copy', 'partnership')}
                  >
                    <Copy className="w-4 h-4 mr-2 text-amber-400" />
                    Copy Link
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Partnership Highlights */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Handshake className="w-6 h-6 text-purple-400" />
                  Lead Developer Partnership
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-purple-400 font-semibold mb-2">Hybrid Compensation</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        15% equity stake (3-year vest)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        15% net profit share (Year 1)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        Quarterly profit distributions
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-purple-400 font-semibold mb-2">3-Year Projections</h4>
                    <ul className="space-y-2 text-gray-300 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        $18M ARR by Year 3
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        ~$1.47M profit share (cash)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-purple-400" />
                        ~$10.8M equity (at exit)
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vesting Schedule */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <PieChart className="w-6 h-6 text-amber-400" />
                  3-Year Vesting Schedule
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-purple-500/20 rounded-xl p-4 text-center border border-purple-500/50">
                    <div className="text-xs text-purple-400 mb-1">YEAR 1</div>
                    <div className="text-xl font-bold text-purple-400">15%</div>
                    <div className="text-gray-400 text-xs">Profit Share</div>
                    <div className="text-gray-500 text-xs mt-1">0% Equity (Cliff)</div>
                  </div>
                  <div className="bg-amber-500/20 rounded-xl p-4 text-center border border-amber-500/50">
                    <div className="text-xs text-amber-400 mb-1">YEAR 2</div>
                    <div className="text-xl font-bold text-purple-400">10%</div>
                    <div className="text-gray-400 text-xs">Profit Share</div>
                    <div className="text-green-400 text-xs mt-1">5% Equity Vested</div>
                  </div>
                  <div className="bg-amber-500/20 rounded-xl p-4 text-center border border-amber-500/50">
                    <div className="text-xs text-amber-400 mb-1">YEAR 3</div>
                    <div className="text-xl font-bold text-purple-400">5%</div>
                    <div className="text-gray-400 text-xs">Profit Share</div>
                    <div className="text-green-400 text-xs mt-1">10% Equity Vested</div>
                  </div>
                  <div className="bg-green-500/20 rounded-xl p-4 text-center border border-green-500/50">
                    <div className="text-xs text-green-400 mb-1">YEAR 4+</div>
                    <div className="text-xl font-bold text-green-400">15%</div>
                    <div className="text-green-300 text-xs">Fully Vested</div>
                    <div className="text-gray-500 text-xs mt-1">0% Profit Share</div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <p className="text-amber-400 text-xs text-center">
                    <strong>Total Potential:</strong> ~$12.3M ($1.47M cash + $10.8M equity at exit)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card className="bg-stone-800/50 border-stone-700">
              <CardContent className="p-6 text-center">
                <Code className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Ready to Partner?</h4>
                <p className="text-gray-400 mb-4">View the full partnership proposal with vesting schedule, legal framework, and financial projections.</p>
                <Link to="/partnership">
                  <Button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                    View Full Proposal
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <p className="text-gray-500 text-xs mt-4">GONOWBABY MEDIA GROUP LLC | MyHairChair</p>
              </CardContent>
            </Card>
          </div>
        );

      case "soldiers":
        return (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 border border-amber-500/30">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-amber-900/20 via-transparent to-transparent" />
              <div className="relative p-8 md:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center">
                    <Shield className="w-7 h-7 text-black" />
                  </div>
                  <div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-1">
                      Founding Partner Program
                    </Badge>
                    <h2 className="text-3xl font-bold text-white">HairChair Soldiers</h2>
                  </div>
                </div>
                <p className="text-xl text-gray-300 mb-6 max-w-3xl">
                  An exclusive, invite-only affiliate program for <strong className="text-amber-400">50 elite salon owners</strong> to build passive income by recruiting stylists to MyHairChair.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/soldiers">
                    <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold">
                      View Program Details
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Market Facts Summary */}
            <Card className="bg-gradient-to-br from-zinc-900 to-black border-amber-500/20">
              <CardHeader>
                <CardTitle className="text-2xl text-amber-400 flex items-center gap-3">
                  <BarChart3 className="w-7 h-7" />
                  Market Fact Summary: The &quot;HairChair Soldiers&quot; Opportunity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Section 1: The Untapped Market */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold">1</span>
                    The Untapped Market
                  </h3>
                  <p className="text-gray-300 mb-4">
                    The U.S. Beauty and Grooming industry is <strong className="text-white">massive, fragmented, and undergoing a digital revolution</strong>. 
                    In 2026, there are between <strong className="text-amber-400">1.1 million and 1.3 million</strong> active salons and barbershops.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Scissors className="w-6 h-6 text-pink-400" />
                        <span className="text-2xl font-bold text-white">~1,000,000</span>
                      </div>
                      <p className="text-gray-400 font-medium">Hair Salons</p>
                      <p className="text-sm text-gray-500">From luxury suites to independent shops, the volume is at an all-time high.</p>
                    </div>
                    <div className="bg-zinc-900/50 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Scissors className="w-6 h-6 text-blue-400" />
                        <span className="text-2xl font-bold text-white">~200,000</span>
                      </div>
                      <p className="text-gray-400 font-medium">Barbershops</p>
                      <p className="text-sm text-gray-500">The <strong className="text-blue-400">fastest-growing sector</strong>, surging as cultural &quot;experience hubs&quot; over the last 5 years.</p>
                    </div>
                  </div>
                </div>

                {/* Section 2: The Problem */}
                <div className="bg-zinc-800/50 rounded-xl p-6 border border-zinc-700">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold">2</span>
                    The Problem: The &quot;Landlord Headache&quot;
                  </h3>
                  <p className="text-gray-300 mb-4">
                    The biggest shift in the industry is the <strong className="text-amber-400">Solo Movement</strong>.
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <p className="text-red-400 font-bold text-lg mb-2">
                      Over 70% of the market now operates on a Booth Rental or Independent Contractor model.
                    </p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                        <Target className="w-5 h-5" />
                        The Pain Point
                      </h4>
                      <p className="text-gray-300">
                        This means there are nearly <strong className="text-white">800,000 Salon Owners acting as &quot;Landlords.&quot;</strong> They spend their days:
                      </p>
                      <ul className="mt-3 space-y-2 text-gray-400">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                          Chasing down booth rent
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                          Managing conflicting schedules
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                          Fighting &quot;manual&quot; payment friction
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-green-400 font-semibold mb-3 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        The Solution
                      </h4>
                      <p className="text-gray-300">
                        <strong className="text-amber-400">MyHairChair</strong> automates the &quot;business&quot; side, allowing Owners to:
                      </p>
                      <ul className="mt-3 space-y-2 text-gray-400">
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          Collect rent automatically
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          Track shop revenue in real-time
                        </li>
                        <li className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          Eliminate awkward conversations
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section 3: The Soldier Advantage */}
                <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/5 rounded-xl p-6 border border-amber-500/30">
                  <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold">3</span>
                    The &quot;Soldier&quot; Advantage: Scarcity &amp; Impact
                  </h3>
                  <p className="text-gray-300 mb-6">
                    We are <strong className="text-white">not looking for 10,000 users</strong>; we are recruiting <strong className="text-amber-400">50 HairChair Soldiers</strong>.
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-zinc-900/80 rounded-lg p-4 text-center">
                      <Award className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                      <h4 className="text-amber-400 font-bold mb-2">Exclusivity</h4>
                      <p className="text-sm text-gray-400">
                        In a sea of <strong className="text-white">1 million shops</strong>, being one of the &quot;Founding 50&quot; puts you in an <strong className="text-amber-400">elite special forces unit</strong> of the industry.
                      </p>
                    </div>
                    <div className="bg-zinc-900/80 rounded-lg p-4 text-center">
                      <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                      <h4 className="text-yellow-400 font-bold mb-2">The Viral Effect</h4>
                      <p className="text-sm text-gray-400">
                        If each Soldier manages a shop of just <strong className="text-white">6–10 stylists</strong>, our 50 Partners will command a network of <strong className="text-yellow-400">300 to 500 active monthly subscribers</strong> almost instantly.
                      </p>
                    </div>
                    <div className="bg-zinc-900/80 rounded-lg p-4 text-center">
                      <Globe className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                      <h4 className="text-blue-400 font-bold mb-2">High Density</h4>
                      <p className="text-sm text-gray-400">
                        Hyper-focused on powerhouse states: <strong className="text-white">California, Texas, Florida, New York, Illinois</strong> and the urban grooming capital, <strong className="text-blue-400">Georgia</strong>.
                      </p>
                    </div>
                  </div>

                  {/* Target States Visual */}
                  <div className="bg-zinc-900/50 rounded-lg p-4">
                    <h4 className="text-white font-semibold mb-3 text-center">Target Markets</h4>
                    <div className="flex flex-wrap justify-center gap-3">
                      {[
                        { state: "California", color: "bg-blue-500" },
                        { state: "Texas", color: "bg-red-500" },
                        { state: "Florida", color: "bg-orange-500" },
                        { state: "New York", color: "bg-purple-500" },
                        { state: "Illinois", color: "bg-cyan-500" },
                        { state: "Georgia", color: "bg-amber-500" }
                      ].map(({ state, color }) => (
                        <Badge key={state} className={`${color} text-white px-3 py-1`}>
                          {state}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 10/10/8 Payout Engine */}
            <Card className="bg-zinc-900/50 border-zinc-700">
              <CardHeader>
                <CardTitle className="text-xl text-white flex items-center gap-2">
                  <DollarSign className="w-6 h-6 text-green-400" />
                  The 10/10/8 Payout Engine
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Pro Tier Commissions */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-bold rounded">PRO</span>
                    <span className="text-gray-400 text-sm">$29.99/month subscription</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 rounded-xl p-5 border border-blue-500/30">
                      <div className="text-3xl font-bold text-blue-400 mb-2">$10</div>
                      <div className="text-white font-semibold mb-1">Month 1 Bonus</div>
                      <p className="text-sm text-gray-400">Pending payout when your referred stylist makes their first payment.</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 rounded-xl p-5 border border-green-500/30">
                      <div className="text-3xl font-bold text-green-400 mb-2">$20</div>
                      <div className="text-white font-semibold mb-1">Month 2 Total</div>
                      <p className="text-sm text-gray-400">Full bonus unlocked ($10 + $10) after second month payment.</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl p-5 border border-amber-500/30">
                      <div className="text-3xl font-bold text-amber-400 mb-2">8%</div>
                      <div className="text-white font-semibold mb-1">Lifetime</div>
                      <p className="text-sm text-gray-400">Ongoing commission from month 3 onwards. Forever.</p>
                    </div>
                  </div>
                </div>
                
                {/* Elite Tier Commissions */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold rounded">ELITE</span>
                    <span className="text-gray-400 text-sm">$49.99/month subscription</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/10 rounded-xl p-5 border border-purple-500/30">
                      <div className="text-3xl font-bold text-blue-400 mb-2">$15</div>
                      <div className="text-white font-semibold mb-1">Month 1 Bonus</div>
                      <p className="text-sm text-gray-400">Pending payout when your referred stylist makes their first payment.</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 rounded-xl p-5 border border-purple-500/30">
                      <div className="text-3xl font-bold text-green-400 mb-2">$30</div>
                      <div className="text-white font-semibold mb-1">Month 2 Total</div>
                      <p className="text-sm text-gray-400">Full bonus unlocked ($15 + $15) after second month payment.</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-500/20 to-yellow-500/10 rounded-xl p-5 border border-purple-500/30">
                      <div className="text-3xl font-bold text-amber-400 mb-2">8%</div>
                      <div className="text-white font-semibold mb-1">Lifetime</div>
                      <p className="text-sm text-gray-400">Ongoing commission from month 3 onwards. Forever.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card className="bg-gradient-to-r from-zinc-800 to-zinc-900 border-amber-500/30">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-white mb-2">Ready to Join the Ranks?</h4>
                <p className="text-gray-400 mb-4">Only 50 founding spots available. Apply now and become a HairChair Soldier.</p>
                <Link to="/soldiers">
                  <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold">
                    Apply Now
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header - Public friendly */}
      <header className="sticky top-0 z-50 bg-stone-950/90 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="/images/myhairchair-logo.png" 
              alt="MyHairChair" 
              className="h-10 sm:h-12 md:h-14 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/register">
              <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold">
                Get Started Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Hero Image Section */}
        <div className="relative rounded-2xl overflow-hidden mb-12 ring-2 ring-amber-500/30 shadow-2xl shadow-amber-500/20">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/bf1d2e2c-705d-447e-ae32-aacc715b9ac1/images/4c1af9627370186efd894b66a574ab50c8e00c566722e49391389229831c5f73.png"
            alt="Professional barbers ready for success with MyHairChair"
            className="w-full h-[300px] sm:h-[350px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>
          <div className="absolute bottom-6 left-6 right-6">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-3">
              <Presentation className="w-3 h-3 mr-1" />
              Demo Day Ready
            </Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              Complete <span className="text-amber-400">Demo Package</span>
            </h1>
            <p className="text-gray-300 max-w-2xl">
              Everything you need to impress investors, onboard clients, and recruit stylists.
            </p>
          </div>
        </div>

        {/* Demo Category Cards */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          {demoCategories.map((category) => (
            <Card 
              key={category.id}
              onClick={() => setActiveDemo(activeDemo === category.id ? null : category.id)}
              className={`cursor-pointer transition-all hover:scale-105 ${
                activeDemo === category.id 
                  ? `ring-2 ring-offset-2 ring-offset-black bg-gradient-to-br ${category.color}` 
                  : 'bg-stone-800/50 border-stone-700 hover:border-stone-600'
              }`}
            >
              <CardContent className="p-4 text-center">
                {category.badge && (
                  <Badge className={`${category.badgeColor} text-white text-xs mb-2`}>
                    {category.badge}
                  </Badge>
                )}
                <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${
                  activeDemo === category.id ? 'bg-white/20' : `bg-gradient-to-br ${category.color}`
                }`}>
                  <category.icon className={`w-6 h-6 ${activeDemo === category.id ? 'text-white' : 'text-white'}`} />
                </div>
                <h3 className={`font-semibold mb-1 ${activeDemo === category.id ? 'text-white' : 'text-white'}`}>
                  {category.title}
                </h3>
                <p className={`text-xs ${activeDemo === category.id ? 'text-white/80' : 'text-gray-400'}`}>
                  {category.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Active Demo Content */}
        {activeDemo && (
          <Card id="demo-content" className="bg-stone-900 border-stone-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                {demoCategories.find(c => c.id === activeDemo)?.icon && (
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${demoCategories.find(c => c.id === activeDemo)?.color} flex items-center justify-center`}>
                    {(() => {
                      const Icon = demoCategories.find(c => c.id === activeDemo)?.icon;
                      return Icon ? <Icon className="w-4 h-4 text-white" /> : null;
                    })()}
                  </div>
                )}
                {demoCategories.find(c => c.id === activeDemo)?.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {renderDemoContent()}
            </CardContent>
          </Card>
        )}

        {/* Quick Links Section */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-400" />
            Quick Access Links
          </h2>
          <div className="grid md:grid-cols-4 gap-4 mb-4">
            <Link to="/">
              <Card className="bg-stone-800/50 border-stone-700 hover:border-amber-500/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <Globe className="w-8 h-8 text-amber-400" />
                  <div>
                    <h4 className="text-white font-semibold">Landing Page</h4>
                    <p className="text-xs text-gray-400">Main marketing page</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/find-stylists">
              <Card className="bg-stone-800/50 border-stone-700 hover:border-blue-500/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-400" />
                  <div>
                    <h4 className="text-white font-semibold">Find Stylists</h4>
                    <p className="text-xs text-gray-400">Client search page</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/membership">
              <Card className="bg-stone-800/50 border-stone-700 hover:border-purple-500/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <Crown className="w-8 h-8 text-purple-400" />
                  <div>
                    <h4 className="text-white font-semibold">Membership</h4>
                    <p className="text-xs text-gray-400">Pricing & plans</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/investors">
              <Card className="bg-stone-800/50 border-stone-700 hover:border-emerald-500/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-emerald-400" />
                  <div>
                    <h4 className="text-white font-semibold">Investors</h4>
                    <p className="text-xs text-gray-400">Full pitch deck</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
          
          {/* New Feature Demos */}
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-green-400" />
            New Feature Demos
            <Badge className="bg-green-500 text-white text-xs">Recently Added</Badge>
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <Link to="/stylist">
              <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/30 hover:border-green-400/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">Walk-in Queue</h4>
                      <Badge className="bg-green-500 text-white text-xs">NEW</Badge>
                    </div>
                    <p className="text-xs text-gray-400">In-person client management</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/stylist">
              <Card className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-cyan-500/30 hover:border-cyan-400/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">Invite Clients</h4>
                      <Badge className="bg-cyan-500 text-white text-xs">NEW</Badge>
                    </div>
                    <p className="text-xs text-gray-400">QR codes & digital business cards</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
            <Link to="/dashboard">
              <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 hover:border-purple-400/50 transition-colors cursor-pointer h-full">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-semibold">Mobile UI</h4>
                      <Badge className="bg-purple-500 text-white text-xs">NEW</Badge>
                    </div>
                    <p className="text-xs text-gray-400">Bottom nav & optimized layouts</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>

        {/* Demo Tips */}
        <Card className="mt-8 bg-gradient-to-br from-amber-500/10 to-yellow-500/10 border-amber-500/30">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Demo Tips for Success
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-amber-400 font-semibold mb-2">For Investors</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Start with the Platform Overview, then Investor Presentation</li>
                  <li>• Highlight the market opportunity and traction metrics</li>
                  <li>• Show live user data from the Admin Dashboard</li>
                </ul>
              </div>
              <div>
                <h4 className="text-amber-400 font-semibold mb-2">For Stylists/Barbers</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Demo the booking flow end-to-end</li>
                  <li>• Show the revenue dashboard and analytics</li>
                  <li>• Highlight the loyalty program and referral features</li>
                </ul>
              </div>
              <div>
                <h4 className="text-amber-400 font-semibold mb-2">For Salon Owners</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Focus on booth management and rent collection</li>
                  <li>• Demo the marketing and CRM tools</li>
                  <li>• Show ROI calculations and time savings</li>
                </ul>
              </div>
              <div>
                <h4 className="text-amber-400 font-semibold mb-2">For Clients</h4>
                <ul className="space-y-1 text-sm text-gray-300">
                  <li>• Show how easy it is to find and book a stylist</li>
                  <li>• Demonstrate the rewards program</li>
                  <li>• Highlight the 24/7 booking convenience</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Get Started CTA - For visitors */}
        <Card className="mt-8 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Scissors className="w-8 h-8 text-black" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Ready to Get Started?</h3>
            <p className="text-gray-400 mb-6 max-w-xl mx-auto">
              Join thousands of stylists, barbers, and salon owners who are growing their business with MyHairChair.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/register?role=stylist">
                <Button className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold px-8 py-6 text-lg">
                  <Scissors className="w-5 h-5 mr-2" />
                  Join as Stylist/Barber
                </Button>
              </Link>
              <Link to="/register?role=owner">
                <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 px-8 py-6 text-lg">
                  <Building2 className="w-5 h-5 mr-2" />
                  Join as Salon Owner
                </Button>
              </Link>
              <Link to="/register?role=client">
                <Button variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10 px-8 py-6 text-lg">
                  <User className="w-5 h-5 mr-2" />
                  Join as Client
                </Button>
              </Link>
            </div>
            <p className="text-gray-500 text-sm mt-6">
              Free to start • No credit card required • Cancel anytime
            </p>
          </CardContent>
        </Card>
      </main>

      {/* App Introduction Modal */}
      {showAppIntro && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="max-w-4xl w-full">
            <AppIntroduction 
              onComplete={() => setShowAppIntro(false)} 
              autoPlay={true}
              showCloseButton={true}
            />
          </div>
        </div>
      )}

      {/* Get Started Guide Modal */}
      {showGuide && (
        <GetStartedGuide 
          userRole={showGuide}
          onComplete={() => setShowGuide(null)}
          onSkip={() => setShowGuide(null)}
        />
      )}
    </div>
  );
};

export default DemoPackagePage;
